/**
 * Validate SIRET number (14 digits)
 * Only checks format, not Luhn checksum (some valid SIRETs don't pass Luhn)
 */
export function validateSiret(siret: string): { valid: boolean; message?: string } {
  // Remove spaces and check if empty
  const cleaned = siret.replace(/\s/g, '');
  
  if (!cleaned) {
    return { valid: true }; // Empty is allowed (optional field)
  }
  
  // Check if all characters are digits
  if (!/^\d+$/.test(cleaned)) {
    return { valid: false, message: 'Le SIRET ne doit contenir que des chiffres' };
  }
  
  // Check length - only error if too many digits
  if (cleaned.length > 14) {
    return { valid: false, message: 'Le SIRET doit contenir exactement 14 chiffres' };
  }
  
  // If exactly 14 digits, it's valid
  // If less than 14, still typing - don't show error
  return { valid: true };
}

/**
 * Validate French postal code (5 digits)
 */
export function validatePostalCode(postalCode: string): { valid: boolean; message?: string } {
  const cleaned = postalCode.replace(/\s/g, '');
  
  if (!cleaned) {
    return { valid: true }; // Empty is allowed (optional field)
  }
  
  if (!/^\d{5}$/.test(cleaned)) {
    return { valid: false, message: 'Le code postal doit contenir 5 chiffres' };
  }
  
  // Check if it's a valid French department code (01-95 + 97x for DOM-TOM + 2A/2B for Corsica)
  const dept = parseInt(cleaned.substring(0, 2), 10);
  if (dept < 1 || (dept > 95 && dept < 97) || dept > 98) {
    // Special case for Corsica (20xxx)
    if (dept !== 20) {
      return { valid: false, message: 'Code postal invalide' };
    }
  }
  
  return { valid: true };
}

/**
 * Format SIRET for display (XXX XXX XXX XXXXX)
 */
export function formatSiret(siret: string): string {
  const cleaned = siret.replace(/\s/g, '');
  if (cleaned.length !== 14) return siret;
  
  return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 9)} ${cleaned.slice(9)}`;
}
